import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../api/apiRoutes";
import { apiFetchJson } from "../../utils/http";
import { getActorType, getClaims } from "../../utils/auth";

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 300;

export default function EessAutocomplete({
  value,
  onChange,
  disabled = false,
  placeholder = "Buscar EE.SS...",
  flota = "SI",
}) {
  const boxRef = useRef(null);
  const debounceRef = useRef(null);
  const eessPrecargadaRef = useRef(false);

  const esActorEess = String(getActorType() || "").toUpperCase() === "EESS";

  // ============================
  //        ESTADOS
  // ============================
  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // ============================
  //   PRECARGAR EESS DEL TOKEN
  // ============================
  useEffect(() => {
    const actorType = String(getActorType() || "").toUpperCase();
    const claims = getClaims();
    const eessId = claims?.eessId;

    if (actorType !== "EESS") return;
    if (!eessId) return;
    if (value) return;
    if (eessPrecargadaRef.current) return;

    eessPrecargadaRef.current = true;

    const cargarEessDelToken = async () => {
      try {
        const data = await apiFetchJson(apiRoutes.eess.detail(eessId), true);

        const raw = data?.eess || data?.item || data;

        const eessNormalizada = {
          ...raw,
          id: raw?.id ?? eessId,
          codigo: raw?.codigo ?? raw?.codigoEstacion ?? raw?.codEstacion ?? "",
          nombre: raw?.nombre ?? raw?.nombreEstacion ?? raw?.descricaoEstacion ?? "",
          label:
            raw?.label ??
            raw?.nombre ??
            raw?.nombreEstacion ??
            raw?.descricaoEstacion ??
            "",
        };

        onChange(eessNormalizada);
        setInputText(eessNormalizada.label || eessNormalizada.nombre || "");
      } catch (error) {
        console.error("No se pudo cargar la EE.SS. del token", error);
      }
    };

    cargarEessDelToken();
  }, [value, onChange]);

  // ============================
  //   SINCRONIZAR VALUE → INPUT
  // ============================
  useEffect(() => {
    if (value?.label) {
      setInputText(value.label);
    } else if (value?.nombre) {
      setInputText(value.nombre);
    }

    if (!value) {
      setInputText("");
    }
  }, [value]);

  // ============================
  //   CLICK AFUERA → CERRAR
  // ============================
  useEffect(() => {
    const handleOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // ============================
  //        FETCH
  // ============================
  const fetchEess = async ({ pageIndex = 0, append = false, query }) => {
    if (loading) return;
    setLoading(true);

    const url = apiRoutes.eessHabilitadas.autocompleteUrl(
      "eess",
      query,
      pageIndex,
      PAGE_SIZE,
      null,
      flota
    );

    try {
      const response = await apiFetchJson(url, true);
      const responseItems = response?.items ?? [];

      setItems((prev) =>
        append ? [...prev, ...responseItems] : responseItems
      );
      setHasMore(!!response?.hasMore);
      setPage(pageIndex);
    } catch {
      if (!append) {
        setItems([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================
  //        INPUT (debounce)
  // ============================
  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputText(text);
    setOpen(true);

    if (value && text.trim() === "") {
      onChange(null);
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchEess({
        pageIndex: 0,
        append: false,
        query: text.trim(),
      });
    }, DEBOUNCE_MS);
  };

  const handleFocus = () => {
    setOpen(true);

    // Siempre hace fetch, incluso si ya hay inputText o items
    fetchEess({
      pageIndex: 0,
      append: false,
      query: inputText.trim(),
    });
  };

  // ============================
  //     SCROLL INFINITO
  // ============================
  const handleScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

    if (nearBottom && hasMore && !loading) {
      fetchEess({
        pageIndex: page + 1,
        append: true,
        query: inputText.trim(),
      });
    }
  };

  // ============================
  //      SELECCIONAR
  // ============================
  const handleSelect = (eess) => {
    const eessNormalizada = {
      ...eess,
      label: eess.label ?? eess.nombre ?? eess.nombreEstacion ?? "",
    };

    onChange(eessNormalizada);
    setInputText(eessNormalizada.label);
    setOpen(false);
  };

  // ============================
  //        LIMPIAR
  // ============================
  const clearSelection = () => {
    onChange(null);
    setInputText("");
    setItems([]);
    setOpen(false);
  };

  // ============================
  //        RENDER
  // ============================
  return (
    <div ref={boxRef} className="relative">
      <input
        type="text"
        className="input"
        disabled={disabled || esActorEess}
        placeholder={placeholder}
        value={inputText}
        onChange={handleInputChange}
        onFocus={handleFocus}
      />

      {value && !esActorEess && (
        <button
          type="button"
          onClick={clearSelection}
          className="absolute right-3 top-2 text-gray-400"
          title="Limpiar"
        >
          ×
        </button>
      )}

      {open && (
        <div
          className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-auto text-sm"
          onScroll={handleScroll}
        >
          {loading && items.length === 0 && (
            <div className="px-3 py-2 text-gray-500">
              Cargando EE.SS...
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="px-3 py-2 text-gray-500">
              Sin resultados.
            </div>
          )}

          {items.map((eess) => (
            <button
              key={eess.id}
              type="button"
              className="block w-full text-left px-3 py-2 hover:bg-emerald-50"
              onClick={() => handleSelect(eess)}
            >
              {eess.codigo && (
                <div className="font-mono text-xs text-gray-700">
                  {eess.codigo}
                </div>
              )}
              <div className="text-sm font-medium">
                {eess.nombre}
              </div>
            </button>
          ))}

          {loading && items.length > 0 && (
            <div className="px-3 py-2 text-gray-400 text-xs">
              Cargando más…
            </div>
          )}
        </div>
      )}
    </div>
  );
}